import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Event } from './events.service';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatChipsModule } from '@angular/material/chips';
import { RouterModule } from '@angular/router';
import {
  formatUTCToLocalWithTimezone,
  formatDateShort,
  getTimezoneAbbreviation,
  getUserTimezone
} from '../shared/timezone.utils';
import { EventShareComponent } from './event-share.component';
import { EventsService, EventRegistrant } from './events.service';
import { inject } from '@angular/core';
import { getOptimizedImageUrl } from '../shared/image.utils';

interface DialogData {
  event: Event;
  canModify: boolean;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isGlobalEventManager?: boolean;
}

@Component({
  selector: 'app-event-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatDialogModule,
    MatButtonModule,
    MatChipsModule,
    RouterModule
],
  template: `
    <div class="event-details-dialog">
      <!-- Header -->
      <div class="dialog-header">
        <div class="header-content">
          <mat-icon class="header-icon">event_available</mat-icon>
          <h2>Event Details</h2>
        </div>
        <button mat-icon-button (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Content -->
      <mat-dialog-content class="dialog-content">
        <!-- Event Title -->
        <div class="section">
          <label>Event Title</label>
          <h3 class="event-title">{{ data.event.title }}</h3>
        </div>

        <!-- Event Image -->
        <div class="event-image" *ngIf="data.event.image" (click)="openLightbox()">
          <img [src]="getEventImageUrl()" [alt]="data.event.title">
          <div class="zoom-hint">
            <mat-icon>zoom_in</mat-icon>
            <span>Click to zoom</span>
          </div>
        </div>

        <!-- Event Details Grid -->
        <div class="details-grid">
          <div class="detail-item">
            <label>Start Date & Time</label>
            <div class="detail-value">
              <mat-icon>calendar_today</mat-icon>
              <span>{{ formatDateTime(data.event.start) }}</span>
            </div>
          </div>

          <div class="detail-item end-with-share">
  <div class="end-date-col">
    <label>End Date & Time</label>
    <div class="detail-value">
      <mat-icon>calendar_today</mat-icon>
      <span>{{ formatDateTime(data.event.end) }}</span>
    </div>
  </div>
  <div class="share-col">
    <label>Share</label>
    <div class="detail-value">
      <button mat-icon-button class="copy-link-inline-btn" (click)="copyEventLink()">
        <mat-icon>{{ linkCopied ? 'check' : 'content_copy' }}</mat-icon>
      </button>
      <span>{{ linkCopied ? 'Link copied!' : 'Copy event link' }}</span>
    </div>
  </div>
</div>
     <div class="section timezone-info">
          <mat-icon>info</mat-icon>
         <span>All times shown here are in your local time zone ({{ getTimezone() }})</span>
        </div>
          <div class="detail-item">
            <label>Event Type</label>
            <div class="detail-value">
              <mat-icon>category</mat-icon>
              <span class="capitalize">{{ data.event.type }}</span>
            </div>
          </div>

        <div class="detail-item">
          <label>Platform</label>
          <div class="detail-value">
            <mat-icon>videocam</mat-icon>
            <span>{{ data.event.platform }}<ng-container *ngIf="data.event.virtualPlatformType"> ({{ data.event.virtualPlatformType }})</ng-container></span>
          </div>
        </div>

          <!-- Location -->
          <div class="detail-item" *ngIf="data.event.location">
            <label>Location</label>
            <div class="detail-value">
              <mat-icon>place</mat-icon>
              <span>{{ data.event.location }}</span>
            </div>
          </div>
<div class="detail-item" *ngIf="data.event.isRecurring">
  <label>Recurrence</label>
  <div class="detail-value">
    <mat-icon>repeat</mat-icon>
    <span>{{ data.event.recurrenceFrequency | titlecase }}, {{ data.event.recurrenceCount }} sessions</span>
  </div>
</div>
          <!-- Max Attendees -->
          <div class="detail-item" *ngIf="data.event.maxAttendees">
            <label>Max Attendees</label>
            <div class="detail-value">
              <mat-icon>people</mat-icon>
              <span>{{ data.event.maxAttendees }}</span>
            </div>
          </div>
        </div>

        <!-- Description -->
        <div class="section description" *ngIf="data.event.description">
          <label>Description</label>
          <p>{{ data.event.description }}</p>
        </div>

        <!-- Target Participants -->
<div class="section" *ngIf="data.isAuthenticated && data.event.participants && data.event.participants.length > 0">
          <label>Target Participants</label>
          <div class="chips-container">
            <mat-chip-set>
              <mat-chip *ngFor="let participant of data.event.participants" class="participant-chip">
                <mat-icon>person</mat-icon>
                <span class="capitalize">{{ participant }}</span>
              </mat-chip>
            </mat-chip-set>
          </div>
        </div>

        <!-- Tags -->
        <div class="section" *ngIf="data.event.tags && data.event.tags.length > 0">
          <label>Tags</label>
          <div class="chips-container">
            <mat-chip-set>
              <mat-chip *ngFor="let tag of data.event.tags" class="tag-chip">
                <mat-icon>label</mat-icon>
                {{ tag }}
              </mat-chip>
            </mat-chip-set>
          </div>
        </div>

<div class="section" *ngIf="hasSuperAdminEventAccess && data.event.targetOrganizationNames && data.event.targetOrganizationNames.length > 0">
  <label>Target Organizations</label>
  <div class="chips-container">
    <mat-chip-set>
      <mat-chip *ngFor="let orgName of data.event.targetOrganizationNames" class="org-chip">
        <mat-icon>business</mat-icon>
        <span>{{ orgName }}</span>
      </mat-chip>
    </mat-chip-set>
  </div>
</div>

<!-- External Attendees — admin-only, since these are private emails -->
<div class="section" *ngIf="data.canModify && data.event.externalAttendeeEmails && data.event.externalAttendeeEmails.length > 0">
  <label>Cohost <mat-icon class="lock-icon" inline>lock</mat-icon></label>
  <div class="chips-container">
    <mat-chip-set>
      <mat-chip *ngFor="let email of data.event.externalAttendeeEmails" class="external-attendee-chip">
        <mat-icon>mail</mat-icon>
        <span>{{ email }}</span>
      </mat-chip>
    </mat-chip-set>
  </div>
</div>
<!-- Registrants — admin-only, shown for public events with registrations -->
<div class="section" *ngIf="data.canModify && isPublicEvent()">
  <label>
    Registrants
    <mat-icon class="lock-icon" inline>lock</mat-icon>
  </label>

  <button mat-stroked-button (click)="toggleRegistrants()" class="toggle-registrants-btn">
    <mat-icon>{{ showRegistrants ? 'expand_less' : 'expand_more' }}</mat-icon>
    {{ showRegistrants ? 'Hide' : 'Show' }} Registrants
    <span *ngIf="registrantsLoaded"> ({{ registrants.length }})</span>
  </button>

  <div class="registrants-panel" *ngIf="showRegistrants">
    <div class="registrants-loading" *ngIf="registrantsLoading">
      <mat-icon class="spin">autorenew</mat-icon>
      <span>Loading registrants…</span>
    </div>

    <div class="registrants-empty" *ngIf="!registrantsLoading && registrantsLoaded && registrants.length === 0">
      No one has registered yet.
    </div>

    <div class="registrants-table" *ngIf="!registrantsLoading && registrants.length > 0">
      <div class="registrant-row registrant-header">
        <span>Name</span>
        <span>Email</span>
        <span>Organization</span>
        <span>Role</span>
        <span>Registered</span>
      </div>
      <div class="registrant-row" *ngFor="let r of registrants">
        <span>{{ r.firstName }} {{ r.lastName }}</span>
        <span>{{ r.email }}</span>
        <span>{{ r.organization || '—' }}</span>
        <span class="capitalize">{{ r.role || '—' }}</span>
        <span>{{ formatDate(r.registeredAt) }}</span>
      </div>
    </div>
  </div>
</div>

       <!-- Meeting/Join Link -->
<div class="section" *ngIf="data.event.meetingUrl">
  <label>Join Meeting</label>
  <a
    [href]="data.event.meetingUrl"
    target="_blank"
    rel="noopener noreferrer"
    mat-stroked-button
    class="join-inline-btn">
    <mat-icon>launch</mat-icon>
    Click to join the meeting
  </a>
  <p class="registration-note">Please ensure you're registered before joining.</p>
</div>
<!-- Host/Admin Meeting Access — visible only to admins/creator -->
<div class="section host-access" *ngIf="data.canModify && hasHostInfo()">
  <label>Host Access <mat-icon class="lock-icon" inline>lock</mat-icon></label>

  <div class="detail-value link-value" *ngIf="data.event.zoomStartUrl">
    <mat-icon>admin_panel_settings</mat-icon>
    <a [href]="data.event.zoomStartUrl" target="_blank" rel="noopener noreferrer">
      Start as Host (Zoom)
    </a>
  </div>

  <div class="detail-value link-value" *ngIf="data.event.externalMeetingHostUrl">
    <mat-icon>admin_panel_settings</mat-icon>
    <a [href]="data.event.externalMeetingHostUrl" target="_blank" rel="noopener noreferrer">
      Open in Calendar ({{ data.event.virtualPlatformType || data.event.platform }})
    </a>
  </div>

  <div class="detail-value" *ngIf="data.event.meetingCode">
    <mat-icon>pin</mat-icon>
    <span>Meeting Code: <strong>{{ data.event.meetingCode }}</strong></span>
  </div>
</div>

<!-- Registration Link -->
<div class="section" *ngIf="data.event.registrationUrl">
  <label>Registration Link</label>
  <div class="detail-value link-value">
    <mat-icon>how_to_reg</mat-icon>
    <a [href]="data.event.registrationUrl" target="_blank" rel="noopener noreferrer">
      {{ data.event.registrationUrl }}
    </a>
  </div>
</div>
<div class="section" *ngIf="!data.event.registrationUrl && isPublicEvent()">
  <label>Registration</label>
  <a
    [routerLink]="['/events', data.event.id, 'register']"
    (click)="close()"
    mat-stroked-button
    class="join-inline-btn">
    <mat-icon>how_to_reg</mat-icon>
    Click here to register
  </a>
  <p class="registration-note">Your joining link will be sent to your email and you'll also receive event reminders.</p>
</div>

        <!-- Event Metadata -->
        <div class="section metadata" *ngIf="data.event.createdAt">
          <label>Event Information</label>
          <div class="metadata-grid">
            <div class="metadata-item" *ngIf="data.event.createdAt">
              <mat-icon>schedule</mat-icon>
              <span>Created: {{ formatDate(data.event.createdAt) }}</span>
            </div>
            <div class="metadata-item" *ngIf="data.event.updatedAt">
              <mat-icon>update</mat-icon>
              <span>Last Updated: {{ formatDate(data.event.updatedAt) }}</span>
            </div>
          </div>
        </div>

        <!-- User Timezone Info -->
   
      </mat-dialog-content>

      <!-- Actions -->
      <mat-dialog-actions class="dialog-actions">
      <div class="actions-grid">

  <button
    *ngIf="data.canModify && !isPastEvent()"
    mat-raised-button
    color="accent"
    (click)="editEvent()">
    <mat-icon>edit</mat-icon>
    Edit Event
  </button>

  <button
    *ngIf="data.canModify"
    mat-raised-button
    class="delete-button"
    (click)="deleteEvent()">
    <mat-icon>delete</mat-icon>
    Delete Event
  </button>
</div>

<div class="share-menu-overlay" *ngIf="shareMenuOpen" (click)="closeShareMenu()">
  <div class="share-menu" (click)="$event.stopPropagation()">
    <div class="share-menu-header">
      <span>Share this event</span>
      <button mat-icon-button (click)="closeShareMenu()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="share-menu-options">
      <button mat-button (click)="shareToWhatsApp(); closeShareMenu()">
        <mat-icon>chat</mat-icon> WhatsApp
      </button>
      <button mat-button (click)="shareToFacebook(); closeShareMenu()">
        <mat-icon>facebook</mat-icon> Facebook
      </button>
      <button mat-button (click)="shareToTwitter(); closeShareMenu()">
        <mat-icon>flutter_dash</mat-icon> Twitter / X
      </button>
      <button mat-button (click)="shareToLinkedIn(); closeShareMenu()">
        <mat-icon>work</mat-icon> LinkedIn
      </button>
      <button mat-button (click)="copyEventLink(); closeShareMenu()">
        <mat-icon>content_copy</mat-icon> Copy Link
      </button>
    </div>
  </div>
</div>
      </mat-dialog-actions>
    </div>

    <!-- Lightbox / Zoom Overlay -->
    <div class="lightbox-overlay" *ngIf="isLightboxOpen" (click)="closeLightbox()">
      <button mat-icon-button class="lightbox-close" (click)="closeLightbox()">
        <mat-icon>close</mat-icon>
      </button>

      <div class="lightbox-controls" (click)="$event.stopPropagation()">
        <button mat-icon-button (click)="zoomOut()" [disabled]="zoomLevel <= minZoom">
          <mat-icon>zoom_out</mat-icon>
        </button>
        <span class="zoom-level">{{ (zoomLevel * 100).toFixed(0) }}%</span>
        <button mat-icon-button (click)="zoomIn()" [disabled]="zoomLevel >= maxZoom">
          <mat-icon>zoom_in</mat-icon>
        </button>
        <button mat-icon-button (click)="resetZoom()">
          <mat-icon>restart_alt</mat-icon>
        </button>
      </div>

      <div class="lightbox-image-wrapper" (click)="$event.stopPropagation()" (wheel)="onWheelZoom($event)">
        <img
           [src]="getLightboxImageUrl()"
          [alt]="data.event.title"
          [style.transform]="'scale(' + zoomLevel + ')'"
          class="lightbox-image">
      </div>
    </div>
  `,
  styles: [`
    .event-details-dialog {
      display: flex;
      flex-direction: column;
      max-height: 90vh;
      width: 100%;
    }
.copy-link-button {
  background-color: #374151 !important;
  color: white !important;

  &:hover {
    background-color: #1f2937 !important;
  }
}
    .dialog-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: linear-gradient(135deg, #007664 0%, #005a4a 100%);
      color: white;
      padding: 1.5rem 2rem;
      margin: -24px -24px 0 -24px;

      .header-content {
        display: flex;
        align-items: center;
        gap: 0.75rem;

        h2 {
          margin: 0;
          font-size: 1.75rem;
          font-weight: 600;
        }

        .header-icon {
          font-size: 2rem;
          width: 2rem;
          height: 2rem;
          color: #a7f3d0;
        }
      }

      button {
        color: white;

        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
      }
    }
.join-button {
  background-color: #007664 !important;
  color: white !important;

  &:hover {
    background-color: #005a4d !important;
  }
}
.share-button {
  background-color: #0284c7 !important;
  color: white !important;

  &:hover {
    background-color: #0369a1 !important;
  }
}

.share-menu-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2100;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  @media (min-width: 600px) {
    align-items: center;
  }
}

.share-menu {
  background: white;
  border-radius: 0.75rem 0.75rem 0 0;
  width: 100%;
  max-width: 400px;
  padding: 1rem;

  @media (min-width: 600px) {
    border-radius: 0.75rem;
  }
}

.share-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.share-menu-options {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  button {
    display: flex;
    justify-content: flex-start;
    gap: 0.75rem;
    padding: 0.75rem;
  }
}
    .dialog-content {
      flex: 1;
      overflow-y: auto;
      padding: 2rem;
    }

    .section {
      margin-bottom: 1.5rem;

      label {
        display: block;
        font-size: 0.875rem;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.75rem;
      }

      &.description {
        border-top: 1px solid #e5e7eb;
        padding-top: 1.5rem;

        p {
          white-space: pre-wrap;
          color: #374151;
          line-height: 1.6;
          margin: 0;
        }
      }

      &.metadata {
        border-top: 1px solid #e5e7eb;
        padding-top: 1.5rem;
      }

      &.timezone-info {
        background-color: #f0fdf4;
        border: 1px solid #dcfce7;
        border-radius: 0.375rem;
        padding: 1rem;
        display: flex;
        align-items: center;
        gap: 0.75rem;
        color: #166534;
        margin-top: 2rem;
        border-top: none;
        padding-top: 1rem;

        mat-icon {
          color: #16a34a;
          font-size: 1.25rem;
          width: 1.25rem;
          height: 1.25rem;
          flex-shrink: 0;
        }

        span {
          font-size: 0.875rem;
          font-weight: 500;
        }
      }
    }

    .event-title {
      font-size: 1.75rem;
      font-weight: 700;
      color: #111827;
      margin: 0;
      text-transform: capitalize;
    }

    .event-image {
      position: relative;
      width: 100%;
      max-height: 28rem;
      border-radius: 0.5rem;
      overflow: hidden;
      margin-bottom: 1.5rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      background-color: #f3f4f6;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

     img {
    width: auto;       
    max-width: 100%;
    height: auto;
    max-height: 28rem;
    object-fit: contain;  
  }

      .zoom-hint {
        position: absolute;
        bottom: 0.75rem;
        right: 0.75rem;
        display: flex;
        align-items: center;
        gap: 0.35rem;
        background-color: rgba(0, 0, 0, 0.6);
        color: white;
        padding: 0.35rem 0.75rem;
        border-radius: 1rem;
        font-size: 0.75rem;
        opacity: 0;
        transition: opacity 0.2s;

        mat-icon {
          font-size: 1rem;
          width: 1rem;
          height: 1rem;
        }
      }

      &:hover .zoom-hint {
        opacity: 1;
      }
    }

    .details-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1.25rem;
      margin-bottom: 1.5rem;

      @media (max-width: 600px) {
        grid-template-columns: 1fr;
      }
    }
.toggle-registrants-btn {
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.registrants-panel {
  margin-top: 1rem;
}

.registrants-loading, .registrants-empty {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
  padding: 1rem 0;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.registrants-table {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  max-height: 280px;
  overflow-y: auto;
}

.registrant-row {
  display: grid;
  grid-template-columns: 1.1fr 1.4fr 0.8fr 1fr 0.9fr;
  gap: 0.75rem;
  padding: 0.6rem 0.9rem;
  font-size: 0.85rem;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }

  &.registrant-header {
    background-color: #f9fafb;
    font-weight: 600;
    color: #374151;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.03em;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr;

    span:nth-child(3), span:nth-child(4) {
      display: none;
    }
  }
}
    .detail-item {
      label {
        display: block;
        font-weight: 600;
        color: #6b7280;
        margin-bottom: 0.5rem;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .detail-value {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #111827;
        font-size: 0.95rem;

        mat-icon {
          color: #007664;
          font-size: 1.25rem;
          width: 1.25rem;
          height: 1.25rem;
          flex-shrink: 0;
        }

        .capitalize {
          text-transform: capitalize;
        }
      }
    }

    .link-value {
      a {
        color: #007664;
        text-decoration: none;
        word-break: break-all;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .chips-container {
      mat-chip-set {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }

      mat-chip {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        font-size: 0.875rem;

        mat-icon {
          font-size: 1rem;
          width: 1rem;
          height: 1rem;
        }

        &.participant-chip {
          background-color: #dbeafe;
          color: #1e40af;

          mat-icon {
            color: #1e40af;
          }
        }

        &.tag-chip {
          background-color: #f3e8ff;
          color: #7c3aed;

          mat-icon {
            color: #7c3aed;
          }
        }
&.external-attendee-chip {
  background-color: #fef3c7;
  color: #92400e;

  mat-icon {
    color: #92400e;
  }
}
        .capitalize {
          text-transform: capitalize;
        }
      }
    }

    .metadata-grid {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .metadata-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #6b7280;
      font-size: 0.875rem;

      mat-icon {
        font-size: 1rem;
        width: 1rem;
        height: 1rem;
        color: #9ca3af;
      }
    }

    .dialog-actions {
      padding: 1.5rem 2rem;
      border-top: 1px solid #e5e7eb;
      background-color: #f9fafb;
    }

    .actions-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 1rem;
      width: 100%;

      button, a {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        font-weight: 500;
      }

      .register-button {
        background-color: #007664 !important;
        color: white !important;

        &:hover {
          background-color: #005a4d !important;
        }
      }
.join-inline-btn {
  border-color: #007664;
  color: #007664;
  margin-top: 0.25rem;

  &:hover {
    background-color: rgba(0, 118, 100, 0.08);
  }
}
      .delete-button {
        background-color: #dc2626 !important;
        color: white !important;

        &:hover {
          background-color: #b91c1c !important;
        }
      }
    }

    .capitalize {
      text-transform: capitalize;
    }

    @media (max-width: 600px) {
      .actions-grid {
        grid-template-columns: 1fr;
      }
    }

    .dialog-content::-webkit-scrollbar {
      width: 6px;
    }

    .dialog-content::-webkit-scrollbar-track {
      background: #f1f5f9;
    }

    .dialog-content::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;

      &:hover {
        background: #94a3b8;
      }
    }

    /* Lightbox styles */
    .lightbox-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.9);
      z-index: 2000;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: zoom-out;
    }

    .lightbox-close {
      position: absolute;
      top: 1rem;
      right: 1rem;
      color: white;
      background-color: rgba(255, 255, 255, 0.1);
      z-index: 2002;

      &:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }
    }
.copy-link-inline-btn {
  color: #007664;
  width: 28px;
  height: 28px;
  line-height: 28px;

  mat-icon {
    font-size: 18px;
    width: 18px;
    height: 18px;
  }
}
    .lightbox-controls {
      position: absolute;
      bottom: 1.5rem;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background-color: rgba(0, 0, 0, 0.6);
      padding: 0.5rem 1rem;
      border-radius: 2rem;
      z-index: 2002;
      cursor: default;

      button {
        color: white;

        &:disabled {
          color: rgba(255, 255, 255, 0.3);
        }
      }

      .zoom-level {
        color: white;
        font-size: 0.875rem;
        min-width: 3rem;
        text-align: center;
      }
    }
.host-access {
  border: 1px solid #fde68a;
  background-color: #fffbeb;
  border-radius: 0.375rem;
  padding: 1rem;

  label {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: #92400e;
  }

  .lock-icon {
    font-size: 1rem;
    width: 1rem;
    height: 1rem;
  }

  .detail-value {
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
.end-with-share {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;

  .end-date-col,
  .share-col {
    flex: 1 1 0;
    min-width: 0; //
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.75rem;
  }
}
    .lightbox-image-wrapper {
      width: 90vw;
      height: 80vh;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      cursor: default;
    }

    .lightbox-image {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      transition: transform 0.15s ease-out;
    }
  `]
})
export class EventDetailsDialogComponent {
  isLightboxOpen = false;
  zoomLevel = 1;
  readonly minZoom = 0.5;
  readonly maxZoom = 3;
  readonly zoomStep = 0.25;

  constructor(
    public dialogRef: MatDialogRef<EventDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {

  }
linkCopied = false;
private eventsService = inject(EventsService);

registrants: EventRegistrant[] = [];
registrantsLoading = false;
registrantsLoaded = false;
showRegistrants = false;
isPastEvent(): boolean {
  return new Date(this.data.event.end) < new Date();
}
 get hasSuperAdminEventAccess(): boolean {
    return this.data.isSuperAdmin || this.data.isGlobalEventManager === true;
  }
toggleRegistrants(): void {
  this.showRegistrants = !this.showRegistrants;

  if (this.showRegistrants && !this.registrantsLoaded) {
    this.loadRegistrants();
  }
}
getEventImageUrl(): string {
  return getOptimizedImageUrl(this.data.event.image, 1200, 630);
}
getLightboxImageUrl(): string {
  return getOptimizedImageUrl(this.data.event.image, 1920, 1080);
}
loadRegistrants(): void {
  this.registrantsLoading = true;
  this.eventsService.getRegistrants(this.data.event.id).subscribe({
    next: (response) => {
      this.registrants = response.data;
      this.registrantsLoaded = true;
      this.registrantsLoading = false;
    },
    error: () => {
      this.registrantsLoading = false;
    }
  });
}
copyEventLink(): void {
  const shareUrl = `${window.location.origin}/events?eventId=${this.data.event.id}`;

  navigator.clipboard.writeText(shareUrl).then(() => {
    this.linkCopied = true;
    setTimeout(() => this.linkCopied = false, 2000);
  }).catch((error) => {
    console.error('Failed to copy link:', error);
  });
}
  formatDateTime(dateString: string): string {
    try {
      return formatUTCToLocalWithTimezone(dateString);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  }
  isPublicEvent(): boolean {
  const participants = this.data.event.participants;
  if (!participants || participants.length === 0) {
    return true;
  }
  return participants.some(p => p?.trim().toLowerCase() === 'public');
}
shareMenuOpen = false;

async shareEvent(): Promise<void> {
  const shareUrl = this.getShareUrl();
  const shareData = {
    title: this.data.event.title,
    text: this.data.event.description || `Join us for ${this.data.event.title}`,
    url: shareUrl
  };

  // Native share sheet — works on most mobile browsers and some desktop browsers
  if (navigator.share) {
    try {
      await navigator.share(shareData);
    } catch (error) {
      // User cancelled the share sheet — not an actual error, ignore silently
    }
  } else {
    // Fallback for browsers without Web Share API support
    this.shareMenuOpen = true;
  }
}

closeShareMenu(): void {
  this.shareMenuOpen = false;
}

private getShareUrl(): string {
  return `${window.location.origin}/events?eventId=${this.data.event.id}`;
}

shareToFacebook(): void {
  const url = encodeURIComponent(this.getShareUrl());
  window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
}

shareToTwitter(): void {
  const url = encodeURIComponent(this.getShareUrl());
  const text = encodeURIComponent(this.data.event.title);
  window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=600,height=400');
}

shareToLinkedIn(): void {
  const url = encodeURIComponent(this.getShareUrl());
  window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=600,height=400');
}

shareToWhatsApp(): void {
  const text = encodeURIComponent(`${this.data.event.title} - ${this.getShareUrl()}`);
  window.open(`https://wa.me/?text=${text}`, '_blank');
}
hasHostInfo(): boolean {
  return !!(
    this.data.event.zoomStartUrl ||
    this.data.event.externalMeetingHostUrl ||
    this.data.event.meetingCode
  );
}

  formatDate(dateString: string): string {
    try {
      return formatDateShort(dateString);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  }

  getTimezone(): string {
    return getTimezoneAbbreviation();
  }

  openLightbox(): void {
    this.isLightboxOpen = true;
    this.zoomLevel = 1;
  }

  closeLightbox(): void {
    this.isLightboxOpen = false;
    this.zoomLevel = 1;
  }

  zoomIn(): void {
    this.zoomLevel = Math.min(this.zoomLevel + this.zoomStep, this.maxZoom);
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.zoomLevel - this.zoomStep, this.minZoom);
  }

  resetZoom(): void {
    this.zoomLevel = 1;
  }

  onWheelZoom(event: WheelEvent): void {
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  editEvent(): void {
    this.dialogRef.close({ action: 'edit', event: this.data.event });
  }

  deleteEvent(): void {
    this.dialogRef.close({ action: 'delete', event: this.data.event });
  }

  close(): void {
    this.dialogRef.close();
  }
}
